import React from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { FormProvider, useForm, useFormContext } from 'react-hook-form'
import { Button, ChevronRight, TextInput, Timeline } from '@demandlocal/components'
import { useAuthGenerateTwoFactorToken } from '../../api/auth/useAuthGenerateTwoFactorToken'
import toast from 'react-hot-toast'
import { useTwoFactorSmsGenerateToken } from '../../api/user/useTwoFactorSmsGenerateToken'
import { isEmpty } from 'lodash'

type Props = {
  setCurrentStep: (step: number) => void
  type: 'authenticator' | 'sms'
}

const TwoFactorSetupStepOne = ({ setCurrentStep, type }: Props) => {
  const formContext = useFormContext()
  const {
    watch,
    getValues,
    formState: { isSubmitting },
  } = formContext

  const { mutate: generateSMSToken } = useTwoFactorSmsGenerateToken()

  const phoneNumberValue = watch('phone')
  const totpCodeValue = watch('totp_code')

  const { data: twoFactorSecret, isLoading: isLoadingTwoFactorSecret } =
    useAuthGenerateTwoFactorToken()

  return (
    <div className={'py-4'}>
      {type === 'authenticator' && (
        <div className={'mb-12'}>
          <Timeline>
            <Timeline.Item title={'Scan this QR Code'}>
              <div className={'border-2 rounded-lg p-4'}>
                {!isLoadingTwoFactorSecret && <QRCodeSVG value={twoFactorSecret} />}
              </div>
            </Timeline.Item>
            <Timeline.Item title={'Type the MFA token received'}>
              <div className={'flex flex-col gap-2'}>
                <div>
                  Use an MFA-enabled device like Google Authenticator or any
                  authenticating app.
                </div>
                <TextInput
                  rules={{ required: 'This field is required' }}
                  inverted
                  name={'totp_code'}
                  autoComplete={'one-time-code'}
                />
              </div>
            </Timeline.Item>
            <Timeline.Item title={'Verify'}>
              <div className={'flex flex-col gap-2'}>
                <div>
                  Keep your tokens safe. We’ll ask them when you log into your account.
                </div>
                <div>
                  <Button
                    disabled={isSubmitting}
                    type={'submit'}
                    name={'submit-2fa-authenticator'}
                    // @ts-ignore
                    rightSlot={<ChevronRight className={'fill-white'} />}>
                    Verify
                  </Button>
                </div>
              </div>
            </Timeline.Item>
          </Timeline>
        </div>
      )}
      {type === 'sms' && (
        <div className={'mb-12'}>
          <Timeline>
            <Timeline.Item title={'Add your phone number'}>
              <div className={'flex flex-col gap-2'}>
                <div>
                  We will send a code to your phone number. Standard SMS rates may apply.
                </div>
                <TextInput
                  rules={{ required: 'This field is required' }}
                  inverted
                  type={'tel'}
                  name={'phone'}
                  autoComplete={'tel'}
                />
              </div>
            </Timeline.Item>
            <Timeline.Item title={'Send SMS'}>
              <div className={'flex flex-col gap-2'}>
                <div>Send a code to your phone number.</div>
                <div>
                  <Button
                    animate={false}
                    disabled={isSubmitting || isEmpty(phoneNumberValue)}
                    type={'button'}
                    onClick={(e) => {
                      e.preventDefault()
                      const toastId = toast.loading('Sending code...')
                      generateSMSToken(
                        { phone_number: getValues('phone') },
                        {
                          onSuccess: () => {
                            toast.success('Code sent!', {
                              id: toastId,
                            })
                          },
                          onError: () => {
                            toast.error('Error sending code', {
                              id: toastId,
                            })
                          },
                        },
                      )
                    }}
                    // @ts-ignore
                    rightSlot={<ChevronRight className={'fill-white'} />}>
                    Send Code
                  </Button>
                </div>
              </div>
            </Timeline.Item>
            <Timeline.Item title={'Type the MFA token received'}>
              <div className={'flex flex-col gap-2'}>
                <div>
                  We sent a code to your phone number. Please type the code you received.
                </div>
                <TextInput
                  rules={{ required: 'This field is required' }}
                  inverted
                  name={'totp_code'}
                  autoComplete={'one-time-code'}
                />
              </div>
            </Timeline.Item>
            <Timeline.Item title={'Verify'}>
              <div className={'flex flex-col gap-2'}>
                <div>
                  Keep your tokens safe. We’ll ask them when you log into your account.
                </div>
                <div>
                  <Button
                    animate={false}
                    disabled={isSubmitting || isEmpty(totpCodeValue)}
                    type={'submit'}
                    name={'submit-2fa-sms'}
                    // @ts-ignore
                    rightSlot={<ChevronRight className={'fill-white'} />}>
                    Verify
                  </Button>
                </div>
              </div>
            </Timeline.Item>
          </Timeline>
        </div>
      )}
    </div>
  )
}

export default TwoFactorSetupStepOne
